import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiConnector } from "../axios/apiClient";
import { USER_REGISTER } from "../axios/AllBaseUrl";
import { useDispatch } from "react-redux";
import {
	FromEvidya,
	other_site_tag,
	playlistname,
} from "../redux/reducers/PlaylistSlice";
import { evidya_data_interface } from "../Interface/evidya";
import { Base64 } from "js-base64";
import VerifyMsgLayout from "./VerifyLayout/VerifyMsgLayout";
/**
 * VerifyPlaylist Component
 * ------------------------
 * This component handles verification and onboarding of users
 * arriving from an external platform (e.g., Evidya).
 *
 * Responsibilities:
 * 1. Decode Base64 data from URL params
 * 2. Register / validate the user via backend API
 * 3. Initialize Redux and localStorage state
 * 4. Redirect users based on role and source context
 * 5. Display verification status feedback
 */

const VerifyPlaylist = () => {
	/**
	 * Prevents repeated decoding on re-renders
	 */
	let [loopString, setLoopString] = useState<string>("");
	/**
	 * Stores decoded and parsed Base64 payload
	 */
	let [decodedata, setDecodedData] = useState<evidya_data_interface>();
	/**
	 * Indicates whether decoded data is valid
	 */
	const [validDecodeData, setValidDecodeData] = useState<boolean>(false);

	/**
	 * Indicates token / registration validity
	 * Used to display verification status to user
	 */
	const [validtoken, setValidtoken] = useState<boolean>(true);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	/**
	 * Base64 encoded payload received from URL
	 */
	const { data } = useParams();

	/**
	 * Reset any previous Evidya data on initial load
	 */
	localStorage.setItem("evidya_data", JSON.stringify(""));

	/**
	 * Decode Base64 URL data and parse JSON payload
	 */
	if (data && loopString === "") {
		try {
			let decodedata = Base64.atob(data);

			if (decodedata) {
				setValidDecodeData(true);
				setLoopString(decodedata);
				setDecodedData(JSON.parse(decodedata));
			}
		} catch (error) {
			setValidDecodeData(false);
			setLoopString("invalid");
			setValidtoken(false);
		}
	}

	/**
	 * Registers or validates user using decoded data
	 * and initializes application state
	 */
	const flag_Registration = async () => {
		if (validDecodeData) {
			try {
				const registration = await ApiConnector(
					"post",
					`${USER_REGISTER}`,
					{
						user_name: decodedata?.user_name,
						user_email: decodedata?.user_email,
						user_phone: decodedata?.user_phone,
						user_pass: "",
						user_conpass: "",
						combined_unique_id: decodedata?.user_id,
						isFromOtherSite: decodedata?.isFromEvidya,
					},
				);

				if (registration.data.ok) {
					/**
					 * Store auth token
					 */
					setValidtoken(true);
					localStorage.setItem(
						"token",
						JSON.stringify(registration.data.data),
					);

					/**
					 * Store playlist name in Redux
					 */
					dispatch(playlistname(decodedata?.assTitle));

					/**
					 * Normalize and store shared tags
					 */
					let tag = decodedata
						? [
								decodedata?.shareCName,
								decodedata?.shareChapName,
								decodedata?.shareSName,
								decodedata?.shareTopicName,
							]
						: [];
					const tagLower = tag?.map((item) => item?.toLowerCase());
					const trimmedTag = tagLower?.map((item) => item?.trim());
					const filteredTag = trimmedTag?.filter(
						(item) => item !== "" && item !== undefined,
					);

					dispatch(other_site_tag(filteredTag));

					/**
					 * Persist decoded data for downstream flows
					 */
					localStorage.setItem(
						"evidya_data",
						JSON.stringify(decodedata),
					);

					setValidDecodeData(false);

					/**
					 * Navigation logic based on source context
					 */

					const incomingString = localStorage.getItem("evidya_data")!;

					const incomingdata = JSON.parse(incomingString);

					if (incomingdata?.isFromEvidya === 1) {
						Number(incomingdata?.where) === 0 &&
							navigate("/createPlaylist");

						let assData = {
							isFromEvidya: incomingdata?.isFromEvidya,
							classId: incomingdata?.assClassId,
							chapId: incomingdata?.assChId,
							topicId: incomingdata?.assTId,
							subjectId: incomingdata?.assSubId,
						};
						dispatch(FromEvidya(assData));
						Number(incomingdata?.where) === 1 &&
							navigate("/playlist");
						// console.log("incomingdata", incomingdata);
						Number(incomingdata?.where) === 4 &&
							navigate("/shareTeacherPlaylist");
						if (
							incomingdata?.isFromEvidya === 1 &&
							Number(incomingdata?.where) === 2 // teacher share playlist view
						) {
							navigate("/conductedExamsDashboard", {
								state: {
									user_name: incomingdata?.user_name,
									user_id: incomingdata?.user_id,
									school_id: incomingdata?.school_id,
									academic_year_id:
										incomingdata?.academic_year_id,
									type_id: incomingdata?.user_type_id,
								},
							});
						}
						if (
							incomingdata?.isFromEvidya === 1 &&
							Number(incomingdata?.where) === 3 // student playlist view
						) {
							navigate("/fetchStudentPlaylist");
						}
					}
				} else {
					setValidDecodeData(false);
					setValidtoken(false);
				}
			} catch (error) {
				setValidtoken(false);
				setValidDecodeData(false);
				setLoopString("invalid");
			}
		}
	};
	/**
	 * Trigger registration once decoding is complete
	 * NOTE: Dependency array intentionally omitted
	 * (consider optimizing to prevent re-renders)
	 */
	useEffect(() => {
		flag_Registration();
	});

	return (
		<>
			<VerifyMsgLayout validtoken={validtoken} />
		</>
	);
};

export default VerifyPlaylist;
